import type {
  ContourPoints, GingerbreadSettings, GingerbreadShapeVersion, ShapeManufacturability, ShapeManufacturabilityIssue, ShapeRounding,
} from './types'

/** Число по-русски: запятая как десятичный разделитель, пробел между разрядами. */
export const formatNumber = (value: number | null | undefined, maxFractionDigits = 2): string =>
  value == null || !Number.isFinite(value)
    ? '—'
    : value.toLocaleString('ru-RU', { maximumFractionDigits: maxFractionDigits })

/** Число с фиксированным числом знаков после запятой: «1,0», «0,3». */
const formatFixed = (value: number, digits: number): string =>
  value.toLocaleString('ru-RU', { minimumFractionDigits: digits, maximumFractionDigits: digits })

/** Процент со знаком (настоящий минус): «−0,4 %», «+0,1 %». */
const formatSignedPct = (value: number): string => {
  const rounded = Math.round(value * 10) / 10
  const sign = rounded > 0 ? '+' : rounded < 0 ? '−' : ''
  return `${sign}${formatFixed(Math.abs(rounded), 1)} %`
}

/**
 * Строка об автоматическом скруглении углов: «Углы скруглены: R 1,5 / 1,0 мм, площадь −0,4 % (макс. отклонение 0,3 мм)».
 * Пока скругление не применялось (выключено или пропущено) — null.
 */
export const formatRounding = (rounding: ShapeRounding | null | undefined): string | null =>
  rounding?.applied
    ? `Углы скруглены: R ${formatFixed(rounding.innerRadiusMm, 1)} / ${formatFixed(rounding.outerRadiusMm, 1)} мм, `
      + `площадь ${formatSignedPct(rounding.areaDeltaPct)} (макс. отклонение ${formatFixed(rounding.maxDeviationMm, 1)} мм)`
    : null

export const formatCm2 = (value: number | null | undefined): string =>
  value == null || !Number.isFinite(value) ? '—' : `${formatNumber(value, 2)} см²`
export const formatGrams = (value: number | null | undefined): string =>
  value == null || !Number.isFinite(value) ? '—' : `${formatNumber(value, 1)} г`
export const formatMm = (value: number | null | undefined, maxFractionDigits = 1): string =>
  value == null || !Number.isFinite(value) ? '—' : `${formatNumber(value, maxFractionDigits)} мм`

/** Габарит формы: «45,6 × 71,1 мм» — ru-RU, не более одного знака после запятой. */
export const formatSizeMm = (width: number | null | undefined, height: number | null | undefined): string =>
  width == null || height == null || !Number.isFinite(width) || !Number.isFinite(height)
    ? '—'
    : `${formatNumber(width, 1)} × ${formatNumber(height, 1)} мм`

/** Парсинг числа из поля ввода: допускает запятую. Пустая строка и мусор — NaN. */
export const parseDecimal = (text: string): number => {
  const cleaned = text.trim().replace(/\s/g, '').replace(',', '.')
  return cleaned === '' ? Number.NaN : Number(cleaned)
}

const detailNumber = (details: Record<string, unknown> | null | undefined, ...keys: string[]): number | null => {
  for (const key of keys) {
    const parsed = Number(details?.[key])
    if (details?.[key] != null && Number.isFinite(parsed)) return parsed
  }
  return null
}

interface IssueContext { settings?: Partial<GingerbreadSettings> | null; manufacturability?: ShapeManufacturability | null }

/** Текст проблемы технологичности по-русски; неизвестный код — сообщение сервиса как есть. */
export function manufacturabilityIssueText(issue: ShapeManufacturabilityIssue, context: IssueContext = {}): string {
  const details = issue.details
  switch (issue.code) {
    case 'GB_NECK_TOO_THIN': {
      const neck = detailNumber(details, 'neck_mm', 'neckMm') ?? context.manufacturability?.minNeckMm ?? null
      const min = detailNumber(details, 'min_neck_mm', 'minNeckMm') ?? context.settings?.minNeckMm ?? null
      return `Перемычка тоньше ${min == null ? 'допустимого' : formatNumber(min)} мм${neck == null ? '' : `: ${formatNumber(neck)} мм`}`
    }
    case 'GB_INNER_CORNER_SHARP': {
      const radius = detailNumber(details, 'inner_radius_mm', 'innerRadiusMm') ?? context.manufacturability?.minInnerRadiusMm ?? null
      const min = detailNumber(details, 'min_inner_radius_mm', 'minInnerRadiusMm') ?? context.settings?.minInnerRadiusMm ?? null
      return `Острый внутренний угол${radius == null ? '' : `: радиус ${formatNumber(radius)} мм`}${min == null ? '' : `, нужно от ${formatNumber(min)} мм`}`
    }
    case 'GB_TOO_SMALL': {
      const size = detailNumber(details, 'size_mm', 'sizeMm')
      const min = detailNumber(details, 'min_size_mm', 'minSizeMm') ?? context.settings?.minSizeMm ?? null
      return `Форма меньше минимального размера${size == null ? '' : `: ${formatNumber(size)} мм`}${min == null ? '' : `, нужно от ${formatNumber(min)} мм`}`
    }
    case 'GB_TOO_LARGE': {
      const size = detailNumber(details, 'size_mm', 'sizeMm')
      const max = detailNumber(details, 'max_size_mm', 'maxSizeMm') ?? context.settings?.maxSizeMm ?? null
      return `Форма больше максимального размера${size == null ? '' : `: ${formatNumber(size)} мм`}${max == null ? '' : `, допустимо до ${formatNumber(max)} мм`}`
    }
    case 'GB_INVALID':
      return 'Контур самопересекается или вырожден'
    default:
      return issue.message || issue.code
  }
}

/** У версии есть векторный контур. */
export const versionHasVector = (version?: Pick<GingerbreadShapeVersion, 'hasVector' | 'contour'> | null): boolean =>
  Boolean(version && (version.hasVector ?? (version.contour?.length ?? 0) >= 3))

/** Ограничивающая рамка контура и размеры по нему. */
export function contourBounds(points: ContourPoints): { minX: number; minY: number; maxX: number; maxY: number } {
  let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity
  for (const [x, y] of points) {
    if (x < minX) minX = x
    if (x > maxX) maxX = x
    if (y < minY) minY = y
    if (y > maxY) maxY = y
  }
  return { minX, minY, maxX, maxY }
}

export const contourPath = (points: ContourPoints): string =>
  points.length ? `M${points.map(([x, y]) => `${Number(x.toFixed(2))} ${Number(y.toFixed(2))}`).join('L')}Z` : ''

/** Данные ошибки ответа гейтвея: код и сообщение (если есть), без предположений о форме. */
export function apiErrorInfo(error: unknown, fallback: string): { code: string; message: string; issues: ShapeManufacturabilityIssue[] } {
  const caught = error as { message?: string; response?: { data?: unknown } }
  const body = caught?.response?.data as Record<string, unknown> | undefined
  const inner = (body?.data && typeof body.data === 'object' ? body.data : body) as Record<string, unknown> | undefined
  const nested = (body?.error && typeof body.error === 'object' ? body.error : inner?.error && typeof inner.error === 'object' ? inner.error : undefined) as Record<string, unknown> | undefined
  const code = String(nested?.code ?? inner?.code ?? inner?.errorCode ?? body?.code ?? '')
  const details = (nested?.details ?? inner?.details) as Record<string, unknown> | undefined
  const rawIssues = Array.isArray(details?.issues) ? details.issues : Array.isArray(inner?.issues) ? inner.issues : []
  const issues = rawIssues.map((raw): ShapeManufacturabilityIssue => {
    const row = (raw ?? {}) as Record<string, unknown>
    return { code: String(row.code ?? ''), message: String(row.message ?? ''), details: (row.details as Record<string, unknown>) ?? null }
  })
  const message = String(nested?.message ?? inner?.message ?? body?.message ?? caught?.message ?? fallback)
  return { code, message: message || fallback, issues }
}
